import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  FilterList,
  Refresh,
  Payment,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { paymentAPI, branchAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const PaymentList = () => {
  const { user, isSuperAdmin, isBranchAdmin } = useAuth();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    branch: '',
    paymentMode: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  useEffect(() => {
    fetchPayments();
    if (isSuperAdmin) {
      fetchBranches();
    }
  }, [page, rowsPerPage, filters]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ status: 'approved' });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await paymentAPI.getAll(params);
      setPayments(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setStats(response.data.pagination?.stats || null);
    } catch (error) {
      console.error('Error fetching payments:', error);
      // Only show error toast for actual API errors, not for empty data
      if (error.response?.status >= 400) {
        toast.error(error.response?.data?.message || 'Failed to load payments');
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (paymentId) => {
    try {
      const response = await paymentAPI.getOne(paymentId);
      setSelectedPayment(response.data.data.payment);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
    }
  };

  const handleVerifyClick = (payment) => {
    setSelectedPayment(payment);
    setRemarks('');
    setVerifyDialog(true);
  };

  const handleVerifySubmit = async () => {
    try {
      setActionLoading(true);
      await paymentAPI.verify(selectedPayment._id, remarks);
      toast.success('Payment verified successfully');
      fetchPayments();
      setVerifyDialog(false);
      setDetailsDialog(false);
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error(error.response?.data?.message || 'Failed to verify payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (payment) => {
    setSelectedPayment(payment);
    setRemarks('');
    setRejectDialog(true);
  };

  const handleRejectSubmit = async () => {
    if (!remarks.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      await paymentAPI.reject(selectedPayment._id, remarks);
      toast.success('Payment rejected');
      fetchPayments();
      setRejectDialog(false);
      setDetailsDialog(false);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error(error.response?.data?.message || 'Failed to reject payment');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Payment sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isSuperAdmin ? 'All Payments' : 'My Branch Payments'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchPayments}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formatAmount(stats.totalAmount || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {stats.pending || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {stats.verified || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Verified</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                  {stats.totalStudents || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total Students</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  name="branch"
                  value={filters.branch}
                  onChange={handleFilterChange}
                  label="Branch"
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Mode</InputLabel>
              <Select
                name="paymentMode"
                value={filters.paymentMode}
                onChange={handleFilterChange}
                label="Payment Mode"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Payment Reference"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Reference</strong></TableCell>
                {isSuperAdmin && <TableCell><strong>Branch</strong></TableCell>}
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Students</strong></TableCell>
                <TableCell><strong>Mode</strong></TableCell>
                <TableCell><strong>Remitted Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow
                    key={payment._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetails(payment._id)}
                  >
                    <TableCell>{payment.paymentReference || '-'}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        {payment.branch?.name || '-'}
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {payment.branch?.code || ''}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>{formatAmount(payment.amount)}</TableCell>
                    <TableCell>{payment.studentCount}</TableCell>
                    <TableCell>
                      <Chip
                        label={payment.paymentMode?.toUpperCase() || 'N/A'}
                        size="small"
                        color={payment.paymentMode === 'online' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(payment.remittedDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status?.toUpperCase() || 'UNKNOWN'}
                        color={getStatusColor(payment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(payment._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {isSuperAdmin && payment.status === 'pending' && (
                        <>
                          <Tooltip title="Verify">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleVerifyClick(payment)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectClick(payment)}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedPayment && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Payment Details
                </Typography>
                <Chip
                  label={selectedPayment.status?.toUpperCase()}
                  color={getStatusColor(selectedPayment.status)}
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Payment Reference</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedPayment.paymentReference}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Branch</Typography>
                  <Typography variant="body1">{selectedPayment.branch?.name} ({selectedPayment.branch?.code})</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{formatAmount(selectedPayment.amount)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Student Count</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{selectedPayment.studentCount}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Remitted Date</Typography>
                  <Typography variant="body1">{formatDate(selectedPayment.remittedDate)}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Payment Details</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Payment Mode</Typography>
                  <Chip
                    label={selectedPayment.paymentMode?.toUpperCase()}
                    color={selectedPayment.paymentMode === 'online' ? 'primary' : 'default'}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>

                {selectedPayment.paymentMode === 'online' && selectedPayment.onlineDetails && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Payment Type</Typography>
                      <Typography variant="body1">{selectedPayment.onlineDetails.paymentType?.toUpperCase()}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Transaction ID</Typography>
                      <Typography variant="body1">{selectedPayment.onlineDetails.transactionId}</Typography>
                    </Grid>
                    {selectedPayment.onlineDetails.upiId && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">UPI ID</Typography>
                        <Typography variant="body1">{selectedPayment.onlineDetails.upiId}</Typography>
                      </Grid>
                    )}
                    {selectedPayment.onlineDetails.bankName && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Bank Name</Typography>
                        <Typography variant="body1">{selectedPayment.onlineDetails.bankName}</Typography>
                      </Grid>
                    )}
                  </>
                )}

                {selectedPayment.paymentMode === 'offline' && selectedPayment.offlineDetails && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Bank Name</Typography>
                      <Typography variant="body1">{selectedPayment.offlineDetails.bankName}</Typography>
                    </Grid>
                    {selectedPayment.offlineDetails.branchName && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Branch Name</Typography>
                        <Typography variant="body1">{selectedPayment.offlineDetails.branchName}</Typography>
                      </Grid>
                    )}
                    {selectedPayment.offlineDetails.accountNumber && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Account Number</Typography>
                        <Typography variant="body1">{selectedPayment.offlineDetails.accountNumber}</Typography>
                      </Grid>
                    )}
                    {selectedPayment.offlineDetails.ifscCode && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">IFSC Code</Typography>
                        <Typography variant="body1">{selectedPayment.offlineDetails.ifscCode}</Typography>
                      </Grid>
                    )}
                    {selectedPayment.offlineDetails.chequeNumber && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Cheque Number</Typography>
                        <Typography variant="body1">{selectedPayment.offlineDetails.chequeNumber}</Typography>
                      </Grid>
                    )}
                    {selectedPayment.offlineDetails.ddNumber && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">DD Number</Typography>
                        <Typography variant="body1">{selectedPayment.offlineDetails.ddNumber}</Typography>
                      </Grid>
                    )}
                    {selectedPayment.offlineDetails.receiptNumber && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Receipt Number</Typography>
                        <Typography variant="body1">{selectedPayment.offlineDetails.receiptNumber}</Typography>
                      </Grid>
                    )}
                  </>
                )}

                {selectedPayment.remarks && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Remarks</Typography>
                    <Typography variant="body1">{selectedPayment.remarks}</Typography>
                  </Grid>
                )}

                {/* Students List */}
                {selectedPayment.students && selectedPayment.students.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Students ({selectedPayment.students.length})</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Form Number</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedPayment.students.map((student) => (
                              <TableRow key={student._id}>
                                <TableCell>{student.formNumber}</TableCell>
                                <TableCell>{student.personalInfo?.nameAsPerTC}</TableCell>
                                <TableCell>{student.personalInfo?.email}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </>
                )}

                {/* Verification Info */}
                {selectedPayment.verifiedBy && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Verification Info</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Verified By</Typography>
                      <Typography variant="body1">{selectedPayment.verifiedBy?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Verified At</Typography>
                      <Typography variant="body1">{formatDate(selectedPayment.verifiedAt)}</Typography>
                    </Grid>
                    {selectedPayment.verificationRemarks && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Verification Remarks</Typography>
                        <Typography variant="body1">{selectedPayment.verificationRemarks}</Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {isSuperAdmin && selectedPayment.status === 'pending' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleVerifyClick(selectedPayment)}
                    disabled={actionLoading}
                  >
                    Verify
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleRejectClick(selectedPayment)}
                    disabled={actionLoading}
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Payment</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Are you sure you want to verify this payment?
          </Alert>
          <TextField
            label="Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleVerifySubmit}
            disabled={actionLoading}
          >
            {actionLoading ? 'Verifying...' : 'Confirm Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Payment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please provide a reason for rejecting this payment.
          </Alert>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={actionLoading || !remarks.trim()}
          >
            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentList;
